import {
    GroupWord,
    Offer,
    ShopItemId,
    ShopMessage,
    ShopMessageId,
    ShopOrder,
    fractionText,
    money,
} from "./shopGame.ts";
import { formatRate } from "../../MathFactsGenerator/PercentGenerator.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

/*
 * The words of Shop Rush (ADR 0131). The engine (shopGame.ts) hands back an id and the
 * figures; this puts them in the player's language. Every key is written out here as a
 * `labelKey` so StringKeys.test.js sees it, as QuizPage's modes do. The figures arrive
 * already written ("$3.65", "45¢"): US money, in every language, because the game is
 * about US coins and bills.
 */

const MESSAGE_KEYS: Record<ShopMessageId, { labelKey: StringKey }> = {
    hintCoins: { labelKey: "flashCards.shopHintCoins" },
    hintChange: { labelKey: "flashCards.shopHintChange" },
    hintTotal: { labelKey: "flashCards.shopHintTotal" },
    hintTotalCents: { labelKey: "flashCards.shopHintTotalCents" },
    hintGroups: { labelKey: "flashCards.shopHintGroups" },
    hintFraction: { labelKey: "flashCards.shopHintFraction" },
    hintFractionSplit: { labelKey: "flashCards.shopHintFractionSplit" },
    hintFractionSlice: { labelKey: "flashCards.shopHintFractionSlice" },
    hintPercentOf: { labelKey: "flashCards.shopHintPercentOf" },
    hintTenthBuild: { labelKey: "flashCards.shopHintTenthBuild" },
    hintTenthDivide: { labelKey: "flashCards.shopHintTenthDivide" },
    hintTenth: { labelKey: "flashCards.shopHintTenth" },
    hintQuarter: { labelKey: "flashCards.shopHintQuarter" },
    hintTip15: { labelKey: "flashCards.shopHintTip15" },
    hintTip18: { labelKey: "flashCards.shopHintTip18" },
    hintBetterBuy: { labelKey: "flashCards.shopHintBetterBuy" },
    hintUnitPrice: { labelKey: "flashCards.shopHintUnitPrice" },
    hintDiscountTax: { labelKey: "flashCards.shopHintDiscountTax" },
    hintOriginalPrice: { labelKey: "flashCards.shopHintOriginalPrice" },
    hintPercentChange: { labelKey: "flashCards.shopHintPercentChange" },
    hintInterest: { labelKey: "flashCards.shopHintInterest" },
    workedCoins: { labelKey: "flashCards.shopWorkedCoins" },
    workedChange: { labelKey: "flashCards.shopWorkedChange" },
    workedTotal: { labelKey: "flashCards.shopWorkedTotal" },
    workedGroups: { labelKey: "flashCards.shopWorkedGroups" },
    workedFraction: { labelKey: "flashCards.shopWorkedFraction" },
    workedDiscountSale: { labelKey: "flashCards.shopWorkedDiscountSale" },
    workedDiscountOff: { labelKey: "flashCards.shopWorkedDiscountOff" },
    workedTax: { labelKey: "flashCards.shopWorkedTax" },
    workedTip10: { labelKey: "flashCards.shopWorkedTip10" },
    workedTip15: { labelKey: "flashCards.shopWorkedTip15" },
    workedTip18: { labelKey: "flashCards.shopWorkedTip18" },
    workedTip20: { labelKey: "flashCards.shopWorkedTip20" },
    workedTip25: { labelKey: "flashCards.shopWorkedTip25" },
    workedBetterBuy: { labelKey: "flashCards.shopWorkedBetterBuy" },
    workedUnitPrice: { labelKey: "flashCards.shopWorkedUnitPrice" },
    workedDiscountTax: { labelKey: "flashCards.shopWorkedDiscountTax" },
    workedOriginalPrice: { labelKey: "flashCards.shopWorkedOriginalPrice" },
    workedPercentChange: { labelKey: "flashCards.shopWorkedPercentChange" },
    workedInterest: { labelKey: "flashCards.shopWorkedInterest" },
    workedInterestOne: { labelKey: "flashCards.shopWorkedInterestOne" },
};

const ITEM_KEYS: Record<ShopItemId, { one: { labelKey: StringKey }; many: { labelKey: StringKey } }> = {
    sticker: {
        one: { labelKey: "flashCards.shopItemSticker" },
        many: { labelKey: "flashCards.shopItemStickerPlural" },
    },
    pencil: {
        one: { labelKey: "flashCards.shopItemPencil" },
        many: { labelKey: "flashCards.shopItemPencilPlural" },
    },
    apple: {
        one: { labelKey: "flashCards.shopItemApple" },
        many: { labelKey: "flashCards.shopItemApplePlural" },
    },
    banana: {
        one: { labelKey: "flashCards.shopItemBanana" },
        many: { labelKey: "flashCards.shopItemBananaPlural" },
    },
    cookie: {
        one: { labelKey: "flashCards.shopItemCookie" },
        many: { labelKey: "flashCards.shopItemCookiePlural" },
    },
    juice: {
        one: { labelKey: "flashCards.shopItemJuice" },
        many: { labelKey: "flashCards.shopItemJuicePlural" },
    },
    milk: {
        one: { labelKey: "flashCards.shopItemMilk" },
        many: { labelKey: "flashCards.shopItemMilkPlural" },
    },
    bread: {
        one: { labelKey: "flashCards.shopItemBread" },
        many: { labelKey: "flashCards.shopItemBreadPlural" },
    },
    sandwich: {
        one: { labelKey: "flashCards.shopItemSandwich" },
        many: { labelKey: "flashCards.shopItemSandwichPlural" },
    },
    notebook: {
        one: { labelKey: "flashCards.shopItemNotebook" },
        many: { labelKey: "flashCards.shopItemNotebookPlural" },
    },
};

/** The container and the things in it, by the word an equal-groups order carries. */
const GROUP_KEYS: Record<GroupWord, { container: { labelKey: StringKey }; thing: { labelKey: StringKey } }> = {
    rolls: {
        container: { labelKey: "flashCards.shopContainerBags" },
        thing: { labelKey: "flashCards.shopThingRolls" },
    },
    crayons: {
        container: { labelKey: "flashCards.shopContainerBoxes" },
        thing: { labelKey: "flashCards.shopThingCrayons" },
    },
    muffins: {
        container: { labelKey: "flashCards.shopContainerTrays" },
        thing: { labelKey: "flashCards.shopThingMuffins" },
    },
    apples: {
        container: { labelKey: "flashCards.shopContainerBaskets" },
        thing: { labelKey: "flashCards.shopThingApples" },
    },
    stickers: {
        container: { labelKey: "flashCards.shopContainerPacks" },
        thing: { labelKey: "flashCards.shopThingStickers" },
    },
    cookies: {
        container: { labelKey: "flashCards.shopContainerJars" },
        thing: { labelKey: "flashCards.shopThingCookies" },
    },
    oranges: {
        container: { labelKey: "flashCards.shopContainerCrates" },
        thing: { labelKey: "flashCards.shopThingOranges" },
    },
    eggs: {
        container: { labelKey: "flashCards.shopContainerCartons" },
        thing: { labelKey: "flashCards.shopThingEggs" },
    },
};

/** The order templates by order kind (and, where the question changes, the question). */
const ORDER_KEYS = {
    coins: { labelKey: "flashCards.shopOrderCoins" },
    change: { labelKey: "flashCards.shopOrderChange" },
    total: { labelKey: "flashCards.shopOrderTotal" },
    groups: { labelKey: "flashCards.shopOrderGroups" },
    fraction: { labelKey: "flashCards.shopOrderFraction" },
    discountSale: { labelKey: "flashCards.shopOrderDiscountSale" },
    discountOff: { labelKey: "flashCards.shopOrderDiscountOff" },
    tax: { labelKey: "flashCards.shopOrderTax" },
    tip: { labelKey: "flashCards.shopOrderTip" },
    betterBuy: { labelKey: "flashCards.shopOrderBetterBuy" },
    unitPrice: { labelKey: "flashCards.shopOrderUnitPrice" },
    markup: { labelKey: "flashCards.shopOrderMarkup" },
    discountTax: { labelKey: "flashCards.shopOrderDiscountTax" },
    originalPrice: { labelKey: "flashCards.shopOrderOriginalPrice" },
    percentUp: { labelKey: "flashCards.shopOrderPercentUp" },
    percentDown: { labelKey: "flashCards.shopOrderPercentDown" },
    interest: { labelKey: "flashCards.shopOrderInterest" },
    interestOne: { labelKey: "flashCards.shopOrderInterestOne" },
} satisfies Record<string, { labelKey: StringKey }>;

export interface ShopText {
    /** "A snack costs $3.65. The customer pays $5.00. Give the change." */
    orderText(order: ShopOrder): string;
    /** The order as read out when the customer arrives: the card text, and for coins the coins themselves ("25¢, 10¢, 1¢."). */
    announceText(order: ShopOrder): string;
    /** A hint or worked answer from the engine, in words. */
    messageText(message: ShopMessage): string;
    /** The item's name for a count: "juice box" for one, "juice boxes" for more. */
    itemName(id: ShopItemId, qty: number): string;
    /** "6 for $4.20" */
    offerText(offer: Offer): string;
}

/** The words, from a `t`. The hook below is this for the interface language; tests give it the English one. */
export function makeShopText(t: (key: StringKey) => string): ShopText {
    const itemName = (id: ShopItemId, qty: number) => t(qty === 1 ? ITEM_KEYS[id].one.labelKey : ITEM_KEYS[id].many.labelKey);
    const offerText = (offer: Offer) =>
        format(t("flashCards.shopOfferTemplate"), { count: offer.count, price: money(offer.cents) });
    const orderText = (order: ShopOrder): string => {
        switch (order.kind) {
            case "coins":
                return t(ORDER_KEYS.coins.labelKey);
            case "change":
                return format(t(ORDER_KEYS.change.labelKey), {
                    paid: money(order.paidCents),
                    price: money(order.priceCents),
                });
            case "total":
                return format(t(ORDER_KEYS.total.labelKey), {
                    lines: order.items.map((line) => `${line.qty} ${itemName(line.id, line.qty)}`).join(", "),
                });
            case "groups":
                return format(t(ORDER_KEYS.groups.labelKey), {
                    groups: order.groups,
                    each: order.each,
                    container: t(GROUP_KEYS[order.word].container.labelKey),
                    thing: t(GROUP_KEYS[order.word].thing.labelKey),
                });
            case "fraction":
                return format(t(ORDER_KEYS.fraction.labelKey), {
                    fraction: fractionText(order.num, order.den),
                    slices: order.slices,
                });
            case "discount":
                return format(t(order.ask === "sale" ? ORDER_KEYS.discountSale.labelKey : ORDER_KEYS.discountOff.labelKey), {
                    rate: formatRate(order.rateTenths),
                    price: money(order.priceCents),
                });
            case "tax":
                return format(t(ORDER_KEYS.tax.labelKey), {
                    rate: formatRate(order.rateTenths),
                    price: money(order.priceCents),
                });
            case "tip":
                return format(t(ORDER_KEYS.tip.labelKey), {
                    rate: formatRate(order.rateTenths),
                    price: money(order.billCents),
                });
            case "betterBuy":
                return format(t(ORDER_KEYS.betterBuy.labelKey), {
                    a: offerText(order.a),
                    b: offerText(order.b),
                });
            case "unitPrice":
                return format(t(ORDER_KEYS.unitPrice.labelKey), {
                    count: order.count,
                    items: itemName(order.item, order.count),
                    total: money(order.totalCents),
                });
            case "markup":
                return format(t(ORDER_KEYS.markup.labelKey), {
                    rate: formatRate(order.rateTenths),
                    cost: money(order.costCents),
                });
            case "discountTax":
                return format(t(ORDER_KEYS.discountTax.labelKey), {
                    price: money(order.priceCents),
                    discount: formatRate(order.discountTenths),
                    tax: formatRate(order.taxTenths),
                });
            case "originalPrice":
                return format(t(ORDER_KEYS.originalPrice.labelKey), {
                    rate: formatRate(order.rateTenths),
                    sale: money(order.saleCents),
                });
            case "percentChange":
                return format(t(order.direction === "up" ? ORDER_KEYS.percentUp.labelKey : ORDER_KEYS.percentDown.labelKey), {
                    from: money(order.fromCents),
                    to: money(order.toCents),
                });
            default:
                return format(t(order.years === 1 ? ORDER_KEYS.interestOne.labelKey : ORDER_KEYS.interest.labelKey), {
                    principal: money(order.principalCents),
                    rate: order.rate,
                    years: order.years,
                });
        }
    };
    return {
        itemName,
        offerText,
        orderText,
        announceText: (order) =>
            order.kind === "coins" ? `${orderText(order)} ${order.coins.map(money).join(", ")}.` : orderText(order),
        messageText: (message) => format(t(MESSAGE_KEYS[message.id].labelKey), message.values),
    };
}

/** The words in the interface language. */
export function useShopText(): ShopText {
    return makeShopText(useTranslation());
}
